import { Input, Component, EventEmitter, Output, AfterViewInit, ElementRef, OnDestroy } from "@angular/core";
import { ActivatedRoute } from "@angular/router";
import { Subscription } from "rxjs";

import * as go from "gojs";
import {first} from 'rxjs/operators';

import { AuthService } from '../../../services/auth.service';
import { DateService } from "../../../common/services/date.service";
import { DialogService } from "../../../common/services/dialog.service";
import { FlowTemplateService } from "../../services/flow-template.service";
import { LoaderService } from "../../../common/services/loader.service";
import { LayoutService } from "../../../layouts/services/layout.service";
import { SubHeaderService } from "../../../core/services/sub-header.service";
import { SubHeader } from "../../../core/models/sub-header.model";
import { HeightWidthService } from "../../../common/services/height-width.service";
import { EventsService } from "../../../services/events.service";

import { FlowTemplate } from "../../../common/models/flow.dto";

@Component({
    templateUrl: "./workflow-builder.component.html",
    standalone: false
})
export class WorkflowBuilderComponent implements AfterViewInit {
  public subHeader = new SubHeader({ title: "Workflow Builder" });
  public showTab: string = "diagram";
  public moduleGroupId: string | null;
  public moduleId: string | null;
  public template: FlowTemplate;
  public versionId: string;
  public versionOptions = [];
  public publishedDate: string;
  public lastEditedBy: string;
  public placeholder: string = "mm/dd/yyyy";
  public saveOptionsVisible: boolean = false;
  public isLoadingVersions: boolean = true;
  get exportUrl(): string{
    return `/api/flow/template/${this.template.workflowId}/${this.template.majorVersion}.${this.template.minorVersion}/export`;
  }

  constructor(
    public readonly route: ActivatedRoute,
    public readonly service: FlowTemplateService,
    public subHeaderService: SubHeaderService,
    public authService: AuthService,
    private dateService: DateService,
    public loader: LoaderService,
    public layoutService: LayoutService,
    public heightWidthService: HeightWidthService,
    public el: ElementRef,
    public _eventsService: EventsService,
    public dialogService: DialogService
  ) { }

  ngOnInit() {
    this.loader.toggleLoader(false);
    this.setEditedTemplate(this.route.snapshot.data.template);
    this.moduleGroupId = (this.route.snapshot.params.moduleGroupId !== undefined) ? this.route.snapshot.params.moduleGroupId : null;
    this.moduleId = (this.route.snapshot.params.moduleId !== undefined) ? this.route.snapshot.params.moduleId : null;
    this.setVersionInfo();
    this.layoutService.update('WorkflowBuilderComponent');
    //window resize
    this._eventsService.create('workflow', window, 'resize', () => this.onResize());
    this.generateSubHeaderText();
  }

  ngAfterViewInit(): void {
    //emit init inital size
    this.onResize();
  }

  ngOnDestroy(): void {
    if (this._eventsService)
      this._eventsService.remove('workflow');
  }

  /**
   * Sends editing container height for diagram h/w calc's
   */
  public onResize(): void {
    // //get editing container
    // let elGoJSEditing: HTMLDivElement = this.el.nativeElement.querySelector('.gojs-editing-container');

    // if (elGoJSEditing != undefined) {
    //   let hw = { editHeight: elGoJSEditing.clientHeight };
    //   this.heightWidthService.sendHW(hw);
    // }
  }

  public generateSubHeaderText(): void {
    if (this.template.name) {
      this.subHeader.title += ": " + this.template.name;
    }
    if (this.versionId) {
      this.subHeader.title += " - " + this.versionId;
    }
    this.subHeaderService.updateSubHeader(this.subHeader);
  }

  public setEditedTemplate(toEdit: FlowTemplate): void {
    this.template = toEdit;
    // this.effectiveStartDateModel = this.dateService.convertDateToIMyDateModel(new Date(this.template.effectiveDate));
  }

  public setTabEvent(tab: string): void {
    this.showTab = tab;
  }

  public startDateChange(e: any): void {
    console.log('new date received', e);
    this.template.effectiveDate = e;
  }

  public showSaveOptions(opt: boolean): void {
    this.saveOptionsVisible = opt;
  }

  public setVersionInfo(): void {
    this.isLoadingVersions = true;
    this.versionId = this.versionFormat(this.template.majorVersion, this.template.minorVersion);
    this.lastEditedBy = (this.template.editorName);
    this.publishedDate = (this.template.isPublished) ? new Date(this.template.effectiveDate).toLocaleDateString("en-US") : "Unpublished";

    this.service.getVersionList(this.template.workflowId).pipe(first()).subscribe((result) => {
      this.versionOptions = [];
      if (result.length > 0) {
        result.forEach((r) => {
          const version = this.versionFormat(r.majorVersion, r.minorVersion);
          const versionString = `Version ${version}`;
          this.versionOptions.push({ id: version, text: versionString });
        });
      } else {
        const version = this.versionFormat(this.template.majorVersion, this.template.minorVersion);
        const versionString = `Version ${version}`;
        this.versionOptions.push({ id: version, text: versionString });
      };
      this.isLoadingVersions = false;
    }, (error) => {
      const version = this.versionFormat(this.template.majorVersion, this.template.minorVersion);
      const versionString = `Version ${version}`;
      this.versionOptions.push({ id: version, text: versionString });
      this.isLoadingVersions = false;
    });
  }

  public versionFormat(majorVersion: number, minorVersion: number): string {
    let minor: string;
    switch (minorVersion.toString().length) {
      case 1:
        {
          minor = `00${minorVersion}`;
          break;
        }
      case 2:
        {
          minor = `0${minorVersion}`;
          break;
        }
      default:
        {
          minor = minorVersion.toString();
          break;
        }
    }
    return parseFloat(`${majorVersion}.${minor}`).toFixed(3);
  }

  //version as change
  public versionAs(id: string): void {
    const idParts = id.split(".");
    const flowId = this.template.workflowId;
    const majorVersion = parseInt(idParts[0], 10);
    const minorVersion = parseInt(idParts[1], 10);
    this.service.getByVersion(flowId, majorVersion, minorVersion, true).pipe(first()).subscribe((result) => {
      this.setEditedTemplate(result);
      this.setVersionInfo();
    });
  }

  public saveAs(id: string): void {
    this.loader.toggleLoader(true);
    this.saveOptionsVisible = false;
    this.template.editorName = this.authService.User.displayName;
    switch (id) {
      case "draft":
        {
          const save$ = (this.moduleGroupId !== null &&
            this.moduleId !== null &&
            this.template.majorVersion === 0 &&
            this.template.minorVersion === 0)
            ? this.service.saveNew(this.moduleGroupId, this.moduleId, this.template)
            : this.service.saveAsDraft(this.template);

          save$.pipe(first()).subscribe((result) => {
            this.template.majorVersion = result.majorVersion;
            this.template.minorVersion = result.minorVersion;
            this.template.isPublished = false;
            this.setVersionInfo();
            this.loader.toggleLoader(false);
          }, (error) => {
            this.loader.toggleLoader(false);
            console.error(error);
            this.dialogService.openDialog("ok", "An error has occurred during saving.  Please try again.  If the error persists, please contact support.");
          });
          break;
        }
      case "publish":
        {
          const save$ = (this.moduleGroupId !== null &&
            this.moduleId !== null &&
            this.template.majorVersion === 0 &&
            this.template.minorVersion === 0)
            ? this.service.saveNew(this.moduleGroupId, this.moduleId, this.template)
            : this.service.saveAsDraft(this.template);

          save$.pipe(first()).subscribe((result) => {
            this.template.majorVersion = result.majorVersion;
            this.template.minorVersion = result.minorVersion;
            this.template.isPublished = false;
            this.setVersionInfo();

            const publish$ = this.service.publish(this.template);
            publish$.pipe(first()).subscribe((publishResult) => {
              this.template.majorVersion = publishResult.majorVersion;
              this.template.minorVersion = publishResult.minorVersion;
              this.template.effectiveDate = publishResult.effectiveDate;
              this.template.isPublished = true;
              this.setVersionInfo();
              this.loader.toggleLoader(false);
            }, (error) => {
              this.loader.toggleLoader(false);
              console.error(error);
              this.dialogService.openDialog("ok", "An error has occurred during publishing.  Please try again.  If the error persists, please contact support.");
              });
          }, (error) => {
            this.loader.toggleLoader(false);
            console.error(error);
            this.dialogService.openDialog("ok", "An error has occurred during saving.  Please try again.  If the error persists, please contact support.");
          });
          break;
        }
      default:
        break;
    }
  }

  public onDialogConfirm(): void {
    this.dialogService.closeDialog();
  }

  public onDialogClose(): void {
    this.dialogService.closeDialog();
  }

}
